
import React, { useEffect, useRef } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import { useDispatch, useSelector } from "react-redux";
import { updateCustomerList } from "./Customer.actions";
import { ColumnTypes } from "commons/table/Constants";
import { DEFAULT_PAGE_SIZE } from "./Customer.reducer";
import { FilterTypes } from "commons/table/Constants";
import { FaEye } from "react-icons/fa";
import { Link } from "react-router-dom";
import ResetListState from "components/ResetListState";
import { formatDateForDisplay } from "services/MomentUtils";
import DismissableModal from "components/DismissableModal";
import CreateCustomerForm from "./CreateCustomerForm";
import { sendCustomersApiServiceAuthenticatedRequest } from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";
import './CustomerList.css';

const CustomerList = () => {
 const dispatch = useDispatch();
  const modalRef = useRef("CustomerList");
  

  const { data, pages, loading, totalElements, listState } = useSelector(
    (state) => state.customerList
  );

  const canManage = hasRoleIn([PERMISSIONS.PERM_CONFIG_TOOL_MANAGE]);

  const renderLastModifiedDateCol = (date) => {
    if (date) {
      return formatDateForDisplay(date);
    }
    return <span className="center">N/A</span>;
  };

  useEffect(() => {
    dispatch(
      updateCustomerList(
        listState.pageSize,
        0,
        listState.sorted,
        listState.filtered
      )
    );
  }, [dispatch]);

  const onUpdateTable = (filters, sort, pageSize, page) => {
    const newListState = {
      page: page,
      pageSize: pageSize,
      sorted: sort ? sort : [],
      filtered: filters,
    };
    dispatch(
      updateCustomerList(
        newListState.pageSize,
        newListState.page,
        newListState.sorted,
        newListState.filtered
      )
    );
  };

  const handleCreateCustomer = (customerData, doneCallback) => {
    sendCustomersApiServiceAuthenticatedRequest(
      "/customers",
      "POST",
      customerData,
      (response) => {
        if(doneCallback) doneCallback();
        showNotification({
          title: "Success",
          message: "Customer created successfully!",
          type: "success",
          position: "tr",
        });
        handleCloseAndReset();
        onUpdateTable(listState.filtered, listState.sorted, listState.pageSize, listState.page);
      },
      (error) => {
        if(doneCallback) doneCallback();
        showNotification({
          title: "Error",
          message: "Error creating customer!",
          type: "error",
          position: "tr",
        });
        console.log(error);
      }
    );
  };

  const handleClose = () => {
    modalRef.current.handleClose();
  }
  
  // State to force reset of form
  const [formKey, setFormKey] = React.useState(0);

  const handleCloseAndReset = () => {
      handleClose();
      setFormKey(prev => prev + 1);
  }

  const columns = [
    {
      header: "Name",
      accessor: "name",
      accessorKey: "name",
      enableSorting: true,
      enableColumnFilter: true,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Short Code",
      accessor: "customerNameAbbreviation",
      accessorKey: "customerNameAbbreviation",
      enableSorting: true,
      enableColumnFilter: true,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Created By",
      accessor: "createdByName",
      accessorKey: "createdByName",
      enableSorting: false,
      enableColumnFilter: false,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Date Created",
      accessor: "creationDate",
      accessorKey: "creationDate",
      show: true,
      enableSorting: true,
      enableColumnFilter: true,
      filterType: FilterTypes.DateRangePicker,
      placeDatePickerAtEnd: true,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => {
        return renderLastModifiedDateCol(value);
      },
    },
    {
      header: "Actions",
      accessor: "action",
      accessorKey: "action",
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) => {
        return (
          <Link variant="primary" to={`/admin/customer/${row.original.id}`}>
            <FaEye /> &nbsp; View
          </Link>
        );
      },
    },
  ];

  return (
    <Container className="list-container" fluid>
      <Row className="codex-version-display-bar align-items-center">
        <Col md={4}>
          <span style={{ float: "left" }}>
            Total Customers:{" "}
            <strong id="user-total-count">{totalElements}</strong>
          </span>
        </Col>
        <Col md={4}>
          <strong style={{ float: "center" }}>Customer List</strong>
        </Col>
        <Col md={4}>
          <div className="d-flex align-items-center justify-content-end">
            {canManage && (
              <span className="mr-2">
                <DismissableModal
                  ref={modalRef}
                  openModalButtonText="+ Add Customer"
                  openModalButtonStyle="info"
                  openModalButtonBlock={true}
                  openModalButtonDisabled={false}
                  openModalButtonId='customer-create-btn-popup'
                  preventCloseWhenClickedOutsideModal={true}
                  onClose={() => setFormKey(prev => prev + 1)}
                  hideFooter={true}
                  title="Create New Customer"
                  size="lg"
                >
                    <CreateCustomerForm
                      key={formKey}
                      onSubmit={(customerData, doneCallback) => {
                        handleCreateCustomer(customerData, doneCallback);
                      }}
                      onCancel={handleCloseAndReset}
                    />
                </DismissableModal>
              </span>
            )}
            <ResetListState
              onReset={() => onUpdateTable([], [{ id: "name", desc: false }], DEFAULT_PAGE_SIZE, 0)}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ServerSideReactTable
            columnDefination={columns}
            data={data}
            loading={loading}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            currentPage={listState.page}
            pageSize={listState.pageSize}
            totalPages={pages}
            totalRecords={
              totalElements === "Loading ..." ? 0 : totalElements
            }
            sorted={listState.sorted}
            filters={listState.filtered}
            enableMultiRowSelection={false}
            onUpdateTableData={onUpdateTable}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default CustomerList;
